import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/permissionsStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * SystemSettingsPage Component
 * 
 * Provides system-level configuration and maintenance operations including:
 * - Cache management (flush and rebuild)
 * - System diagnostics
 * - Performance optimization tools
 */
const SystemSettingsPage = () => {
    const { apiFetch } = useAuth();
    const [isFlushing, setIsFlushing] = useState(false);
    const [cacheStatus, setCacheStatus] = useState(null);
    const [statusInterval, setStatusInterval] = useState(null);

    // Maintenance Mode: in-memory only on the server (never persisted to a database) -
    // see IExternalActionGuard.SetMaintenanceMode. While on, ExternalActionGuard disables
    // every external-action capability system-wide and maintenance-mode-aware background
    // services (auto-remediation, scheduled tasks, inbound/outbound email, router config
    // pushes, workflow automation, etc.) skip their work cycles entirely.
    const [maintenanceMode, setMaintenanceMode] = useState(null); // null until first load
    const [isTogglingMaintenance, setIsTogglingMaintenance] = useState(false);

    /**
     * Polls the cache status endpoint to track progress of cache rebuild operations
     */
    const startStatusPolling = () => {
        // Clear any existing interval
        if (statusInterval) {
            clearInterval(statusInterval);
        }

        // Poll every 2 seconds
        const interval = setInterval(async () => {
            try {
                const response = await apiFetch('/api/Settings/cache-status');
                const data = await response.json();
                
                if (data.success) {
                    setCacheStatus(data.data);
                    
                    // Stop polling if not warming up
                    if (!data.data.isWarmingUp) {
                        clearInterval(interval);
                        setStatusInterval(null);
                        setIsFlushing(false);
                    }
                }
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error fetching cache status:', error);
            }
        }, 2000);

        setStatusInterval(interval);
    };

    /**
     * Triggers a full cache flush and rebuild operation
     */
    const handleFlushCaches = async () => {
        try {
            setIsFlushing(true);
            
            const response = await apiFetch('/api/Settings/flush-caches', {
                method: 'POST'
            });
            
            const data = await response.json();
            
            if (data.success) {
                toast.success('Cache flush started successfully');
                startStatusPolling();
            } else {
                toast.error(data.message || 'Failed to flush caches');
                setIsFlushing(false);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error flushing caches:', error);
            toast.error('An error occurred while flushing caches');
            setIsFlushing(false);
        }
    };

    /**
     * Fetches the current cache status
     */
    const handleCheckStatus = async () => {
        try {
            const response = await apiFetch('/api/Settings/cache-status');
            const data = await response.json();
            
            if (data.success) {
                setCacheStatus(data.data);
            } else {
                toast.error('Failed to fetch cache status');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching cache status:', error);
            toast.error('An error occurred while fetching cache status');
        }
    };

    // Clean up polling interval on unmount
    useEffect(() => {
        return () => {
            if (statusInterval) {
                clearInterval(statusInterval);
            }
        };
    }, [statusInterval]);

    /**
     * Fetches the current Maintenance Mode state.
     */
    const fetchMaintenanceMode = async () => {
        try {
            const response = await apiFetch('/api/Settings/maintenance-mode');
            const data = await response.json();
            if (data.success) {
                setMaintenanceMode(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching maintenance mode:', error);
        }
    };

    // Load Maintenance Mode state on mount, then poll every 15s so it stays in sync if
    // another admin (or another browser tab) toggles it.
    useEffect(() => {
        fetchMaintenanceMode();
        const interval = setInterval(fetchMaintenanceMode, 15000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Enables or disables Maintenance Mode, with a confirmation prompt in both directions
     * since this is a system-wide safety switch (not persisted - resets on API restart).
     */
    const handleToggleMaintenanceMode = async () => {
        const enabling = !maintenanceMode?.enabled;
        const confirmMessage = enabling
            ? 'Enable Maintenance Mode?\n\nAll auto-remediation, scheduled tasks, inbound/outbound email, router config pushes, and other automated actions will stop system-wide until you turn this back off. This is NOT saved to the database - if the API restarts, it resets to OFF.'
            : 'Disable Maintenance Mode?\n\nThe system will resume normal automated operation immediately.';

        if (!window.confirm(confirmMessage)) return;

        try {
            setIsTogglingMaintenance(true);
            const response = await apiFetch('/api/Settings/maintenance-mode', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled: enabling })
            });
            const data = await response.json();

            if (data.success) {
                setMaintenanceMode(data.data ? { ...data.data, enabledAtUtc: enabling ? new Date().toISOString() : null } : { enabled: enabling });
                toast.success(data.message || `Maintenance mode ${enabling ? 'enabled' : 'disabled'}.`);
                // Refresh from the server to pick up the authoritative enabledBy/enabledAtUtc.
                fetchMaintenanceMode();
            } else {
                toast.error(data.message || 'Failed to update maintenance mode');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error toggling maintenance mode:', error);
            toast.error('An error occurred while updating maintenance mode');
        } finally {
            setIsTogglingMaintenance(false);
        }
    };

    return (
        <div className="permissions-management">
            <h1>System Settings</h1>
            <p className="page-description">
                Manage system-level configurations and perform maintenance operations.
            </p>

            {/* Maintenance Mode Section */}
            <div
                className="settings-section"
                style={{
                    border: maintenanceMode?.enabled ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: maintenanceMode?.enabled ? '#fdeaea' : undefined
                }}
            >
                <h2>🛠️ Maintenance Mode</h2>
                <p className="section-description">
                    While enabled, automated actions are disabled system-wide: auto-remediation, scheduled tasks,
                    inbound/outbound email processing, router configuration pushes, workflow automation, and every
                    other capability gated by the safety guard. Turn this on before importing a full MySQL/InfluxDB
                    backup, then turn it back off once you've confirmed the new data looks correct.
                </p>
                <p style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginTop: '-8px' }}>
                    This setting is NOT saved to the database — it's in-memory only on the API and resets to OFF
                    if the API process restarts.
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', flexShrink: 0 }}>
                        <input
                            type="checkbox"
                            checked={!!maintenanceMode?.enabled}
                            onChange={handleToggleMaintenanceMode}
                            disabled={isTogglingMaintenance || maintenanceMode === null}
                            style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                            style={{
                                position: 'absolute', cursor: isTogglingMaintenance ? 'wait' : 'pointer',
                                top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: maintenanceMode?.enabled ? '#dc3545' : '#ccc',
                                borderRadius: '26px', transition: '.2s'
                            }}
                        >
                            <span
                                style={{
                                    position: 'absolute', height: '20px', width: '20px',
                                    left: maintenanceMode?.enabled ? '27px' : '3px', bottom: '3px',
                                    backgroundColor: 'white', borderRadius: '50%', transition: '.2s'
                                }}
                            />
                        </span>
                    </label>
                    <strong style={{ color: maintenanceMode?.enabled ? '#dc3545' : '#333' }}>
                        {maintenanceMode === null
                            ? 'Loading...'
                            : maintenanceMode.enabled
                                ? 'MAINTENANCE MODE ACTIVE — system is offline for automation'
                                : 'System Online'}
                    </strong>
                </div>

                {maintenanceMode?.enabled && (
                    <div style={{ marginTop: '10px', fontSize: '13px', color: '#721c24' }}>
                        Enabled by <strong>{maintenanceMode.enabledBy || 'unknown'}</strong>
                        {maintenanceMode.enabledAtUtc && (
                            <> at {new Date(maintenanceMode.enabledAtUtc).toLocaleString()}</>
                        )}
                    </div>
                )}
            </div>

            {/* Cache Management Section */}
            <div className="settings-section">
                <h2>Cache Management</h2>
                <p className="section-description">
                    The application caches frequently accessed data to improve performance. 
                    Use the button below to flush all caches and rebuild them with fresh data from the database.
                    This operation may take several minutes to complete.
                </p>

                <div className="cache-controls">
                    <button 
                        className="btn btn-primary"
                        onClick={handleFlushCaches}
                        disabled={isFlushing}
                    >
                        {isFlushing ? 'Flushing Caches...' : 'Flush and Rebuild Caches'}
                    </button>

                    <button 
                        className="btn btn-secondary"
                        onClick={handleCheckStatus}
                        disabled={isFlushing}
                        style={{ marginLeft: '10px' }}
                    >
                        Check Status
                    </button>
                </div>

                {/* Cache Status Display */}
                {cacheStatus && (
                    <div className="cache-status" style={{ marginTop: '20px' }}>
                        <h3>Cache Status</h3>
                        <div className="status-grid" style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '15px',
                            marginTop: '10px'
                        }}>
                            <div className="status-item">
                                <strong>Status:</strong> {cacheStatus.message}
                            </div>
                            <div className="status-item">
                                <strong>Progress:</strong> {cacheStatus.displayCompleted} / {cacheStatus.displayTotal} jobs ({cacheStatus.percent}%)
                            </div>
                            {cacheStatus.startedAt && (
                                <div className="status-item">
                                    <strong>Started At:</strong> {new Date(cacheStatus.startedAt).toLocaleString()}
                                </div>
                            )}
                            {cacheStatus.lastUpdated && (
                                <div className="status-item">
                                    <strong>Last Updated:</strong> {new Date(cacheStatus.lastUpdated).toLocaleString()}
                                </div>
                            )}
                        </div>

                        {/* Progress Bar */}
                        {cacheStatus.isWarmingUp && (
                            <div className="progress-bar-container" style={{ 
                                marginTop: '15px',
                                width: '100%',
                                height: '30px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '5px',
                                overflow: 'hidden'
                            }}>
                                <div 
                                    className="progress-bar" 
                                    style={{
                                        width: `${cacheStatus.percent}%`,
                                        height: '100%',
                                        backgroundColor: '#4CAF50',
                                        transition: 'width 0.3s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {cacheStatus.percent}%
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Future Sections */}
            <div className="settings-section" style={{ marginTop: '30px' }}>
                <h2>System Information</h2>
                <p className="section-description">
                    Additional system information and diagnostics will be available here in future updates.
                </p>
            </div>
        </div>
    );
};

export default SystemSettingsPage;
